'use client'
import * as React from 'react'
import { Check, Copy, Download, ExternalLink, Link2, MessageSquareText, QrCode } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ColorPicker } from '@/components/ui/color-picker'
import { encodeQR, qrToSvg } from '@/lib/widget/qr'

// Google review link + QR generator. Everything runs in the browser:
// the visitor supplies a Place ID (or pastes a g.page / writereview URL
// we can lift one from), we build the canonical direct-review link,
// render a real QR via the self-contained encoder in lib/widget/qr.ts,
// and prefill copy-paste ask templates for email / SMS / packaging.
// Honest split: Google is the source of truth for Place IDs, so we link
// their official Place ID finder instead of pretending to search.

const PLACE_ID_FINDER =
  'https://developers.google.com/maps/documentation/javascript/examples/places-placeid-finder'
const DEFAULT_DARK = '#0b1120'
const QUIET_ZONE = 4

function reviewLinkFor(placeId: string): string {
  return `https://search.google.com/local/writereview?placeid=${encodeURIComponent(placeId)}`
}

// Accepts a raw Place ID, a full writereview URL, or a maps URL that
// carries a placeid= query param. Returns the extracted Place ID or ''.
function extractPlaceId(input: string): string {
  const raw = input.trim()
  if (!raw) return ''
  const fromParam = raw.match(/[?&]placeid=([^&\s]+)/i)
  if (fromParam) return decodeURIComponent(fromParam[1])
  // Place IDs are opaque tokens (typically ChIJ…), no spaces, no slashes.
  if (/^[A-Za-z0-9_-]{16,}$/.test(raw) && !raw.startsWith('http')) return raw
  return ''
}

function askTemplates(link: string, business: string) {
  const name = business.trim() || 'us'
  return {
    email: `Subject: A quick favour?\n\nHi {{first name}},\n\nThanks again for choosing ${name}. If you have 60 seconds, a Google review makes a real difference to a small business like ours, and it helps other people decide with confidence.\n\nLeave a review here: ${link}\n\nIf anything wasn't right, reply to this email instead and we'll fix it.\n\nThank you!\n${name}`,
    sms: `Thanks for shopping with ${name}! If you have a minute, a Google review helps us more than you'd think: ${link}`,
    insert: `Enjoying your order? A 60-second Google review helps ${name} grow. Scan the code or visit the short link on your receipt.`,
  }
}

export function ReviewLinkGenerator() {
  const [input, setInput] = React.useState('')
  const [business, setBusiness] = React.useState('')
  const [dark, setDark] = React.useState(DEFAULT_DARK)
  const [copied, setCopied] = React.useState<string | null>(null)

  const placeId = extractPlaceId(input)
  const link = placeId ? reviewLinkFor(placeId) : ''
  const templates = askTemplates(link || 'https://search.google.com/local/writereview?placeid=YOUR_PLACE_ID', business)

  const matrix = React.useMemo(() => {
    if (!link) return null
    try {
      return encodeQR(link)
    } catch {
      return null
    }
  }, [link])

  const svgMarkup = React.useMemo(
    () => (matrix ? qrToSvg(matrix, { dark, light: '#ffffff', size: 300, quietZone: QUIET_ZONE }) : ''),
    [matrix, dark],
  )

  function copy(text: string, key: string) {
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(key)
      window.setTimeout(() => setCopied(null), 1800)
    })
  }

  function downloadBlob(blob: Blob, filename: string) {
    const href = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = href
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(href)
  }

  function onDownloadSvg() {
    if (!matrix) return
    const blob = new Blob([qrToSvg(matrix, { dark, light: '#ffffff', size: 1024, quietZone: QUIET_ZONE })], {
      type: 'image/svg+xml',
    })
    downloadBlob(blob, 'google-review-qr.svg')
  }

  function onDownloadPng() {
    if (!matrix) return
    const svg = qrToSvg(matrix, { dark, light: '#ffffff', size: 1024, quietZone: QUIET_ZONE })
    const img = new Image()
    const svgBlob = new Blob([svg], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(svgBlob)
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = 1024
      canvas.height = 1024
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, 1024, 1024)
        ctx.drawImage(img, 0, 0, 1024, 1024)
        canvas.toBlob((blob) => blob && downloadBlob(blob, 'google-review-qr.png'), 'image/png')
      }
      URL.revokeObjectURL(url)
    }
    img.src = url
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
      <div className="rounded-2xl border bg-card p-6">
        <label className="block text-sm font-medium" htmlFor="rl-place">
          Your Google Place ID (or paste a review URL that contains one)
        </label>
        <Input
          id="rl-place"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="ChIJN1t_tDeuEmsRUsoyG83frY4"
          className="mt-2 font-mono text-sm"
        />
        <p className="mt-2 text-xs text-muted-foreground">
          Find yours in ~20 seconds with{' '}
          <a
            href={PLACE_ID_FINDER}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline underline-offset-2"
          >
            Google&apos;s official Place ID finder
            <ExternalLink className="ml-0.5 inline h-3 w-3" aria-hidden />
          </a>
          : search your business name, copy the ID that appears on the map pin.
        </p>

        <label className="mt-5 block text-sm font-medium" htmlFor="rl-business">
          Business name (used in the ask templates)
        </label>
        <Input
          id="rl-business"
          value={business}
          onChange={(e) => setBusiness(e.target.value)}
          placeholder="Your store"
          className="mt-2"
        />

        <div className="mt-5">
          <p className="text-sm font-medium">QR colour</p>
          <div className="mt-2">
            <ColorPicker value={dark} onChange={setDark} />
          </div>
        </div>

        {link ? (
          <div className="mt-6 rounded-xl border bg-muted/40 p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Your direct review link
            </p>
            <div className="mt-2 flex items-center gap-2">
              <code className="min-w-0 flex-1 truncate rounded bg-background px-2 py-1.5 text-xs">{link}</code>
              <Button size="sm" variant="outline" onClick={() => copy(link, 'link')}>
                {copied === 'link' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                <span className="ml-1.5">{copied === 'link' ? 'Copied' : 'Copy'}</span>
              </Button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Opens Google&apos;s write-a-review box for your listing directly. Test it in a private window before you
              print anything.
            </p>
          </div>
        ) : (
          <div className="mt-6 rounded-xl border border-dashed p-4 text-sm text-muted-foreground">
            <Link2 className="mr-1.5 inline h-4 w-4" aria-hidden />
            Enter a Place ID above and your link, QR and templates generate instantly. Nothing leaves your browser.
          </div>
        )}
      </div>

      <div className="flex flex-col gap-6">
        <div className="rounded-2xl border bg-card p-6">
          <p className="flex items-center gap-2 text-sm font-medium">
            <QrCode className="h-4 w-4 text-primary" aria-hidden /> Printable QR
          </p>
          <div className="mt-4 flex items-center justify-center rounded-xl border bg-white p-6">
            {svgMarkup ? (
              // Our own encoder generates this SVG from the sanitised link.
              <div className="h-[180px] w-[180px] [&_svg]:h-full [&_svg]:w-full" dangerouslySetInnerHTML={{ __html: svgMarkup }} />
            ) : (
              <div className="flex h-[180px] w-[180px] items-center justify-center rounded bg-muted text-xs text-muted-foreground">
                QR appears here
              </div>
            )}
          </div>
          <div className="mt-4 flex gap-2">
            <Button size="sm" variant="outline" disabled={!matrix} onClick={onDownloadPng}>
              <Download className="mr-1.5 h-4 w-4" /> PNG
            </Button>
            <Button size="sm" variant="outline" disabled={!matrix} onClick={onDownloadSvg}>
              <Download className="mr-1.5 h-4 w-4" /> SVG
            </Button>
          </div>
        </div>

        <div className="rounded-2xl border bg-card p-6">
          <p className="flex items-center gap-2 text-sm font-medium">
            <MessageSquareText className="h-4 w-4 text-primary" aria-hidden /> Ask templates
          </p>
          {(
            [
              { key: 'email', label: 'Email', text: templates.email },
              { key: 'sms', label: 'SMS / WhatsApp', text: templates.sms },
              { key: 'insert', label: 'Packaging insert', text: templates.insert },
            ] as const
          ).map((t) => (
            <div key={t.key} className="mt-4 first:mt-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t.label}</p>
                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => copy(t.text, t.key)}>
                  {copied === t.key ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  <span className="ml-1">{copied === t.key ? 'Copied' : 'Copy'}</span>
                </Button>
              </div>
              <pre className="mt-1.5 max-h-40 overflow-auto whitespace-pre-wrap rounded-lg border bg-muted/40 p-3 text-xs leading-relaxed">
                {t.text}
              </pre>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
